import { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { useMutation } from '@apollo/client';

import { useLogInspectorState } from 'hooks/useLogInspectorState';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import ConfirmDialog from 'components/common/Dialog/ConfirmDialog';
import { ActionsExecutor, DataRow, EventAction } from 'plugins/types';

import { DEFAULT_ACTION_SUCCESS_MESSAGE } from './actions';
import { executeAction, handleChangeSearchAction, handleRedirectAction } from './event-action-handler';
import { APPLY_CONSOLE_ACTION, CREATE_CONSOLE_ACTION_PAYLOAD } from './queries';
import { getExtendedData } from './utils';

/**
 * EventActionsExecutor doesn't render a component, it only displays a confirmation dialog if needed.
 * Its purpose is to handle the action execution, regardless of which component would trigger it.
 *
 * @param param.action                   action selected for execution
 * @param param.values                   the data of this log (event)
 * @param param.actionCompleteCallback   callback on action complete (done or canceled)
 * @param param.initialData              endpoint data passed from outer component
 */
const EventActionsExecutor: ActionsExecutor = ({ action, actionCompleteCallback, values, initialData }) => {
  const extendedData = getExtendedData(values, initialData?.s1Endpoint);

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const { preventLogInspectorFromClosing, allowLogInspectorToClose } = useLogInspectorState();

  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);

  const [s1CreateConsoleAction] = useMutation(CREATE_CONSOLE_ACTION_PAYLOAD);
  const [s1PerformGenericAgentAction] = useMutation(APPLY_CONSOLE_ACTION);
  const setSearchParams = useSetSearchParams();

  const handleActionSuccess = (actionConfig: EventAction, result: number) => {
    if (actionConfig.getSuccessMessage) {
      const successMessage = actionConfig.getSuccessMessage(extendedData, result) || DEFAULT_ACTION_SUCCESS_MESSAGE;
      setPageAlertConfig({ message: successMessage, variant: PageAlertVariant.SUCCESS, autoHide: true });
    }
  };

  const handleActionFailure = (errorMsg: string) => {
    setPageAlertConfig({ message: errorMsg, variant: PageAlertVariant.ERROR, autoHide: true });
  };

  const applyAPIAction = (actionConfig: EventAction, entryData: DataRow) => {
    executeAction(actionConfig, entryData, s1PerformGenericAgentAction)
      .then((result: number) => {
        handleActionSuccess(actionConfig, result);
      })
      .catch((err) => {
        handleActionFailure(err.message);
      });
  };

  const applyAction = () => {
    if (!action || !extendedData) {
      return;
    }
    if (action.actionHandler) {
      handleChangeSearchAction(action, extendedData, setSearchParams);
    } else if (action.redirectPath) {
      handleRedirectAction(action, extendedData, s1CreateConsoleAction)
        .catch((err) => {
          handleActionFailure(err.message);
        })
        .finally(() => {
          actionCompleteCallback();
        });
    } else if (action.getConfirmationMessage) {
      const confirmationMessage = action.getConfirmationMessage(extendedData);
      setConfirmationMessage(confirmationMessage);
      preventLogInspectorFromClosing();
      setShowConfirmationDialog(true);
    } else {
      applyAPIAction(action, extendedData);
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmationDialog(false);
    allowLogInspectorToClose();
    actionCompleteCallback();
  };

  const handleConfirm = () => {
    if (action) {
      applyAPIAction(action, extendedData);
    }
    allowLogInspectorToClose();
    setShowConfirmationDialog(false);
  };

  useEffect(() => {
    if (action) {
      applyAction();
    }
  }, [action]);

  return (
    <ConfirmDialog
      title="Confirm"
      text={confirmationMessage}
      show={showConfirmationDialog}
      close={handleCloseConfirmation}
      onSubmit={handleConfirm}
      textStyleClassName="confirm-action"
    />
  );
};

export default EventActionsExecutor;
